<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';

// Teletel's own face for the agent copilot. It owns the header, the empty
// state, the message list and the input, so nothing here depends on Captain's
// components, labels, icons or prompts. The prompts offered are the ones the
// Teletel responder can actually answer: the open conversation and the
// company's approved knowledge, nothing else.
const props = defineProps({
  messages: { type: Array, default: () => [] },
  isThinking: { type: Boolean, default: false },
  hasConversation: { type: Boolean, default: false },
});

const emit = defineEmits(['send', 'reset', 'close', 'useReply']);

const { t } = useI18n();

const draft = ref('');
const textareaRef = ref(null);
const listRef = ref(null);

// Conversation prompts need an open conversation; account prompts (queues,
// contacts) work anywhere. All of them are things the responder can actually
// do, through Teletel::Copilot::Actions or the approved knowledge.
const CONVERSATION_PROMPTS = ['SUMMARIZE', 'DRAFT', 'MISSING', 'RATE', 'NOTE'];
const KNOWLEDGE_PROMPTS = ['PRIORITY_QUEUE', 'WAITING_QUEUE', 'POLICY'];

const prompts = computed(() =>
  (props.hasConversation ? CONVERSATION_PROMPTS : []).concat(KNOWLEDGE_PROMPTS)
);

const hasMessages = computed(() => props.messages.length > 0);

const formatted = content =>
  new MessageFormatter(content || '').formattedMessage;

const lastAssistantId = computed(() => {
  const assistant = props.messages.filter(m => m.message_type === 'assistant');
  return assistant.length ? assistant[assistant.length - 1].id : null;
});

const autosize = () => {
  const el = textareaRef.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
};

const send = text => {
  const content = (text ?? draft.value).trim();
  if (!content || props.isThinking) return;
  emit('send', content);
  draft.value = '';
  nextTick(autosize);
};

const onEnter = event => {
  if (event.isComposing) return;
  event.preventDefault();
  send();
};

const scrollToBottom = async () => {
  await nextTick();
  if (listRef.value) listRef.value.scrollTop = listRef.value.scrollHeight;
};

watch(() => [props.messages.length, props.isThinking], scrollToBottom);
</script>

<template>
  <div class="flex h-full w-full flex-col text-sm leading-6 text-n-slate-12">
    <div
      class="flex items-center justify-between border-b border-n-weak px-3 py-2"
    >
      <span class="flex items-center gap-2 font-medium">
        <span class="i-lucide-sparkles size-4 text-n-blue-11" />
        {{ t('TELETEL_COPILOT.TITLE') }}
      </span>
      <div class="flex items-center gap-1">
        <button
          v-if="hasMessages"
          type="button"
          class="grid size-7 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
          :title="t('TELETEL_COPILOT.RESET')"
          :aria-label="t('TELETEL_COPILOT.RESET')"
          @click="emit('reset')"
        >
          <span class="i-lucide-refresh-ccw size-4" />
        </button>
        <button
          type="button"
          class="grid size-7 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
          :aria-label="t('TELETEL_COPILOT.CLOSE')"
          @click="emit('close')"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>
    </div>

    <div ref="listRef" class="flex-1 overflow-y-auto px-4 py-4">
      <div v-if="!hasMessages" class="flex flex-col items-center gap-3 pt-6">
        <span class="i-lucide-bot size-9 text-n-blue-11" />
        <p class="max-w-[18rem] text-center text-n-slate-11">
          {{ t('TELETEL_COPILOT.INTRO') }}
        </p>
        <p
          v-if="!hasConversation"
          class="max-w-[18rem] text-center text-xs text-n-slate-10"
        >
          {{ t('TELETEL_COPILOT.NO_CONVERSATION') }}
        </p>
        <div class="mt-2 flex w-full flex-col gap-2">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('TELETEL_COPILOT.TRY') }}
          </span>
          <button
            v-for="key in prompts"
            :key="key"
            type="button"
            class="rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-start text-n-slate-12 hover:bg-n-alpha-2"
            @click="send(t(`TELETEL_COPILOT.PROMPTS.${key}`))"
          >
            {{ t(`TELETEL_COPILOT.PROMPTS.${key}`) }}
          </button>
        </div>
      </div>

      <div v-else class="flex flex-col gap-5">
        <div
          v-for="item in messages"
          :key="item.id"
          class="flex flex-col gap-1"
        >
          <template v-if="item.message_type === 'user'">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('TELETEL_COPILOT.YOU') }}
            </span>
            <p class="whitespace-pre-wrap break-words">{{ item.content }}</p>
          </template>
          <template v-else>
            <span
              class="flex items-center gap-1 text-xs font-medium text-n-blue-11"
            >
              <span class="i-lucide-sparkles size-3" />
              {{ t('TELETEL_COPILOT.TITLE') }}
            </span>
            <!-- What was done for the agent before answering (a lookup ran,
                 a priority changed, a note was saved). -->
            <span
              v-if="item.reasoning"
              class="inline-flex w-fit items-center gap-1 rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11"
            >
              <span class="i-lucide-zap size-3" />
              {{ item.reasoning }}
            </span>
            <div
              v-dompurify-html="formatted(item.content)"
              class="prose-sm break-words"
            />
            <div
              v-if="item.reply_suggestion"
              class="mt-1 flex flex-col gap-2 rounded-lg border border-n-weak bg-n-solid-2 p-3"
            >
              <span class="text-xs font-medium text-n-slate-11">
                {{ t('TELETEL_COPILOT.SUGGESTED_REPLY') }}
              </span>
              <p class="whitespace-pre-wrap break-words">
                {{ item.reply_suggestion }}
              </p>
              <button
                v-if="item.id === lastAssistantId && hasConversation"
                type="button"
                class="self-start rounded-lg bg-n-blue-9 px-3 py-1 text-xs font-medium text-white hover:brightness-110"
                @click="emit('useReply', item.reply_suggestion)"
              >
                {{ t('TELETEL_COPILOT.USE_REPLY') }}
              </button>
            </div>
          </template>
        </div>

        <div
          v-if="isThinking"
          class="flex items-center gap-2 text-xs text-n-slate-11"
        >
          <span class="flex gap-1">
            <span
              class="size-1.5 animate-bounce rounded-full bg-n-blue-9 [animation-delay:-0.3s]"
            />
            <span
              class="size-1.5 animate-bounce rounded-full bg-n-blue-9 [animation-delay:-0.15s]"
            />
            <span class="size-1.5 animate-bounce rounded-full bg-n-blue-9" />
          </span>
          {{ t('TELETEL_COPILOT.THINKING') }}
        </div>
      </div>
    </div>

    <form class="relative mx-3 mb-3 mt-1" @submit.prevent="send()">
      <textarea
        ref="textareaRef"
        v-model="draft"
        rows="1"
        :placeholder="t('TELETEL_COPILOT.ASK')"
        class="reset-base mb-0 w-full resize-none overflow-hidden rounded-lg border border-n-weak bg-n-alpha-3 py-3 pe-12 ps-4 text-sm text-n-slate-12 focus:border-n-blue-11 focus:outline-none focus:ring-2 focus:ring-n-blue-11"
        @input="autosize"
        @keydown.enter.exact="onEnter"
      />
      <button
        type="submit"
        class="absolute end-1 top-1/2 flex h-9 w-10 -translate-y-1/2 items-center justify-center text-n-slate-11 hover:text-n-blue-11 disabled:opacity-40"
        :disabled="isThinking || !draft.trim()"
        :aria-label="t('TELETEL_COPILOT.SEND')"
      >
        <span class="i-lucide-arrow-up size-4" />
      </button>
    </form>
  </div>
</template>
